import ReusableModal from '../common/ReusableModal'
import useStore from '../../store'
import { colors } from '../../utilities/colors'

export default function TransportationAgreementModal() {
  const { dealer, signTransportationAgreement, isSigningTransportationAgreement } = useStore()
  const signed = dealer?.attributes?.['transportation-agreement-signed-at']

  return (
    <ReusableModal
      isOpen={!signed}
      headerText="Transportation User Agreement"
      submitText="Accept"
      handleSubmit={signTransportationAgreement}
      dataTestId="transportation-agreement-modal"
      loading={isSigningTransportationAgreement}
    >
      <p className="pb-4">
        Please review the Transportation User Agreement below. By clicking &quot;Accept,&quot; you confirm that you have read and agree to the terms.
      </p>
      <div
        className="overflow-y-auto rounded-md p-4 text-sm leading-relaxed"
        style={{ maxHeight: '300px', backgroundColor: colors.grey }}
      >
        <p className="pb-2 font-bold">Floorplan Xpress, LLC</p>
        <p className="pb-3 font-bold">Transportation Services Supplemental Terms and Conditions</p>

        <p className="pb-3">
          These Supplemental Terms and Conditions (&quot;Transportation Terms&quot;) are incorporated into and made part of the Floorplan Xpress, LLC
          Terms of Use (&quot;FX Terms&quot;) available at www.floorplanxpress.com. By requesting or arranging transportation services through the
          Floorplan Xpress Dealer Portal (&quot;FX Portal&quot;), you (&quot;Dealer&quot;) agree to be bound by these Transportation Terms in addition
          to the FX Terms. In the event of a conflict between these Transportation Terms and the FX Terms, these Transportation Terms shall control
          with respect to transportation services only.
        </p>

        <p className="pb-2 font-bold">1. Nature of Service, FX Role</p>
        <p className="pb-3">
          Floorplan Xpress, LLC (&quot;FX&quot;) does not provide vehicle transportation services. FX arranges access to vehicle transportation
          services through Carpool Logistics, Inc. dba Carpool (&quot;Carpool&quot;), a federally licensed property broker registered with the Federal
          Motor Carrier Safety Administration under Docket No. MC-1224498. Carpool, in turn, arranges transportation through independent third party
          motor carriers (&quot;Carriers&quot;).
        </p>
        <p className="pb-3">
          FX is not a carrier, transporter, freight forwarder, or logistics provider. FX does not take custody of, transport, handle, or assume
          liability for any vehicle arranged for transport through the FX Portal.
        </p>
        <p className="pb-2">By using the transportation feature in the FX Portal, Dealer acknowledges and agrees that:</p>
        <ul className="list-disc space-y-1 pb-3 pl-6">
          <li>The actual transportation of Dealer&apos;s vehicle will be performed by an independent Carrier selected by Carpool;</li>
          <li>Carpool operates solely as a property broker and does not transport, take custody of, or assume liability for Dealer&apos;s vehicle;</li>
          <li>FX&apos;s role is limited to providing portal access to obtain quotes, submit booking requests, and facilitate payment;</li>
          <li>Neither FX nor Carpool is liable for loss or damage to any vehicle during transport, such liability resting solely with the Carrier.</li>
        </ul>

        <p className="pb-2 font-bold">2. Booking and Confirmation</p>
        <p className="pb-3">
          When Dealer submits a transport request through the FX Portal, the request is transmitted to Carpool via an application programming
          interface (API). A booking is not confirmed until a Carrier accepts the request. FX makes no representation or warranty that a Carrier will
          be available or will accept any booking request.
        </p>
        <p className="pb-3">
          Dealer is solely responsible for ensuring the accuracy of all information submitted in connection with a transport request, including
          vehicle description (year, make, model, trim, body style), vehicle identification number (VIN), condition, point of origin, and destination.
          Any errors or omissions in the submitted information may result in additional fees, cancellation of the booking, or inadequate insurance
          coverage.
        </p>

        <p className="pb-2 font-bold">3. Dealer Responsibilities</p>
        <p className="pb-2">Dealer assumes full responsibility for the following in connection with any transport booking:</p>
        <ul className="list-disc space-y-2 pb-3 pl-6">
          <li>
            <span className="font-semibold">Vehicle Condition and Disclosure.</span> Dealer must accurately disclose the vehicle&apos;s condition,
            including any inoperable status, modifications from original equipment manufacturer (OEM) specifications, oversized components, or other
            non-standard features at the time of booking. Failure to disclose may result in additional carrier charges, refusal to transport, or
            cancellation fees, all of which are the sole responsibility of Dealer.
          </li>
          <li>
            <span className="font-semibold">Vehicle Preparation.</span> Dealer is responsible for preparing the vehicle for transport including
            securing or removing loose parts, removing exterior-mounted racks, disarming alarm systems or providing disengagement instructions, and
            ensuring the vehicle is tendered with no more than one quarter tank of fuel.
          </li>
          <li>
            <span className="font-semibold">Inspection at Pickup.</span> Dealer or Dealer&apos;s designated representative must be present at the
            point of origin to inspect the vehicle with the Carrier, note any pre-existing damage on the Carrier&apos;s Bill of Lading or Carrier
            Form, and sign and retain a copy of same. Dealer is strongly urged to photograph the vehicle from all angles prior to pickup. Failure to
            be present or to document preexisting damage may impair or eliminate Dealer&apos;s ability to pursue a damage claim.
          </li>
          <li>
            <span className="font-semibold">Inspection at Delivery.</span> Dealer or Dealer&apos;s designated representative must be present at the
            destination to inspect the vehicle upon delivery, note any new damage on the Bill of Lading prior to signing, and retain a signed copy.
            Signing the Bill of Lading at delivery without noting damage, regardless of lighting or weather conditions, constitutes confirmation that
            the vehicle was received in satisfactory condition. The Carrier will have no further responsibility following a clean Bill of Lading
            signature.
          </li>
          <li>
            <span className="font-semibold">High Value Vehicles.</span> If the vehicle&apos;s declared value exceeds $100,000, Dealer must notify FX
            at the time of booking. Standard cargo insurance maintained by Carriers may not provide sufficient coverage for vehicles valued above
            $100,000. FX makes no representation or warranty regarding the adequacy of any Carrier&apos;s insurance coverage. Failure to notify FX of
            a high declared value may result in inadequate coverage in the event of loss or damage.
          </li>
          <li>
            <span className="font-semibold">Personal Property.</span> Neither FX, Carpool, nor the Carrier assumes any liability for personal items
            left in the vehicle. Explosives, weapons, ammunition, flammable materials, live animals, controlled substances, and illegal goods are
            strictly prohibited.
          </li>
        </ul>

        <p className="pb-2 font-bold">4. Damage Claims &mdash; IMPORTANT TIME LIMITATION</p>
        <p className="pb-2 font-bold">DEALER MUST NOTIFY FX OF ANY DAMAGE WITHIN 24 HOURS OF DELIVERY</p>
        <p className="pb-3">
          Any damage claim not noted on the Bill of Lading at delivery and not reported within 24 hours of delivery is permanently waived. FX will not
          be responsible for damage claims that are not timely reported in accordance with this requirement.
        </p>
        <p className="pb-2">If Dealer believes a vehicle has been damaged in transit, Dealer must:</p>
        <ul className="list-disc space-y-1 pb-3 pl-6">
          <li>Note all damage on the Bill of Lading prior to signing at delivery;</li>
          <li>Photograph all damage at the time of delivery;</li>
          <li>Report the claim to FX in writing within 24 hours of delivery;</li>
          <li>Retain all documentation including the signed Bill of Lading, photographs, and any correspondence with the Carrier.</li>
        </ul>
        <p className="pb-3">
          Dealer acknowledges that the party liable for cargo loss or damage is the Carrier, not FX or Carpool. Damage claims are governed by federal
          law, including the Carmack Amendment, 49 U.S.C. &sect;14706. FX will provide reasonable assistance in connecting Dealer with Carpool to
          facilitate the claims process, but FX assumes no liability for any cargo loss or damage and is not obligated to reimburse Dealer for any
          damage, rental costs, storage fees, or other expenses arising from a transport incident. Dealer is encouraged to seek independent legal
          counsel at Dealer&apos;s own expense in connection with any claim.
        </p>

        <p className="pb-2 font-bold">5. Cancellation Policy</p>
        <p className="pb-3">
          Dealer may cancel a transport booking at no cost prior to a Carrier accepting the booking. Once a Carrier has accepted the booking,
          cancellation is subject to a minimum cancellation fee of $200.00 plus any additional fees imposed by the Carrier, including but not limited
          to dryrun fees.
        </p>
        <p className="pb-3">
          Carrier acceptance of a booking may occur prior to the physical pickup of the vehicle. Dealer may not cancel without fee simply because the
          vehicle has not yet been picked up.
        </p>
        <p className="pb-3">
          Cancellation requests must be submitted through the FX Portal or in writing to FX. FX will transmit the cancellation request to Carpool. All
          applicable cancellation fees will be charged to Dealer&apos;s account in accordance with Section 6 below.
        </p>

        <p className="pb-2 font-bold">6. Fees and Payment</p>
        <p className="pb-3">
          The transportation fee quoted through the FX Portal includes Carpool&apos;s brokerage fee plus a service fee charged by FX. The total
          transportation fee will be handled in one of the following ways, as applicable to Dealer&apos;s account:
        </p>
        <ul className="list-disc space-y-2 pb-3 pl-6">
          <li>
            <span className="font-semibold">Floored Vehicles.</span> For vehicles that are floored with FX, the total transportation fee will be added
            to the vehicle&apos;s principal balance and will accrue interest at the rate applicable to that vehicle under Dealer&apos;s Floor Plan
            Agreement.
          </li>
          <li>
            <span className="font-semibold">Non Floored Vehicles.</span> For vehicles that are not floored with FX, the total transportation fee will
            be debited from Dealer&apos;s account on file via ACH. Dealer authorizes FX to initiate such ACH debit upon booking confirmation. Dealer
            is responsible for maintaining sufficient funds in the account on file to cover transport fees.
          </li>
        </ul>
        <p className="pb-3">
          Dealer agrees to pay all transportation fees in full without offset, chargeback, or reduction. Outstanding balances will accrue interest and
          may result in suspension of Dealer&apos;s access to transportation services through the FX Portal.
        </p>

        <p className="pb-2 font-bold">7. Delays and Service Interruptions</p>
        <p className="pb-3">
          Estimated pickup and delivery dates provided through the FX Portal are estimates only. Neither FX nor Carpool guarantees pickup or delivery
          dates or times. Delays may occur due to weather, road conditions, government regulations, mechanical issues, or other factors outside
          FX&apos;s or Carpool&apos;s control. FX is not responsible or liable for any losses, costs, or expenses arising from transport delays of
          any kind.
        </p>

        <p className="pb-2 font-bold">8. Limitation of Liability</p>
        <p className="pb-3">
          TO THE FULLEST EXTENT PERMITTED BY LAW, FX&apos;S TOTAL LIABILITY TO DEALER IN CONNECTION WITH ANY TRANSPORT BOOKING SHALL NOT EXCEED THE
          TOTAL TRANSPORTATION FEE PAID BY DEALER TO FX FOR THAT SPECIFIC BOOKING. FX SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
          CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO ANY TRANSPORT BOOKING, REGARDLESS OF THE THEORY OF LIABILITY.
        </p>
        <p className="pb-3">
          FX MAKES NO WARRANTIES, EXPRESS OR IMPLIED, REGARDING THE TRANSPORTATION SERVICES ARRANGED THROUGH THE FX PORTAL, INCLUDING BUT NOT LIMITED
          TO WARRANTIES OF TIMELINESS, FITNESS FOR A PARTICULAR PURPOSE, OR FREEDOM FROM DAMAGE OR LOSS.
        </p>

        <p className="pb-2 font-bold">9. Disputes Regarding Transportation Services</p>
        <p className="pb-3">
          Any dispute regarding the physical transport of a vehicle, including but not limited to damage claims, delays, and carrier conduct, must be
          directed to Carpool Logistics and the applicable Carrier. FX will provide Dealer with available contact information for Carpool and will
          provide reasonable assistance in facilitating communication. FX is not a party to any dispute between Dealer and a Carrier and assumes no
          obligation to resolve or indemnify Dealer in connection with such disputes.
        </p>

        <p className="pb-2 font-bold">10. Third Party Terms</p>
        <p className="pb-3">
          Dealer acknowledges that transportation services arranged through the FX Portal are subject to the terms and conditions of Carpool
          Logistics, Inc. and the applicable Carrier. Dealer may request a copy of Carpool&apos;s terms directly from Carpool. Dealer may also be
          subject to the Carrier&apos;s terms, tariffs, and rules, copies of which must be requested directly from the Carrier.
        </p>

        <p className="pb-2 font-bold">11. Modifications</p>
        <p className="pb-3">
          FX reserves the right to modify these Transportation Terms at any time. Updated terms will be posted to the FX Portal and/or the FX
          website. Dealer&apos;s continued use of the transportation feature following the posting of updated terms constitutes acceptance of the
          revised terms.
        </p>

        <p className="pb-2 font-bold">12. Governing Law</p>
        <p className="pb-3">
          These Transportation Terms shall be governed by the laws of the State of Oklahoma, consistent with the FX Terms of Use. Nothing in these
          Transportation Terms shall be construed to alter the governing law or jurisdiction provisions of the FX Terms of Use.
        </p>

        <p className="pb-2 font-bold">Dealer Acceptance</p>
        <p className="pb-3">
          By clicking to accept these Transportation Terms or by requesting transportation services through the FX Portal, Dealer acknowledges that
          Dealer has read, understands, and agrees to these Transportation Terms in their entirety. This acceptance applies to all future transport
          bookings made through the FX Portal unless and until Dealer receives and accepts updated terms.
        </p>

        <p className="italic">
          These Transportation Terms are supplemental to and incorporated into the Floorplan Xpress, LLC Terms of Use. These Transportation Terms do
          not create any warranty, guarantee, or liability on the part of Floorplan Xpress, LLC with respect to the physical transportation of any
          vehicle.
        </p>
      </div>
    </ReusableModal>
  )
}
